import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Mail, Loader2, ArrowLeft, CheckCircle, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ClientLogin = () => {
  const { photographerId } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [photographer, setPhotographer] = useState(null);
  const [loadingPhotographer, setLoadingPhotographer] = useState(true);
  const [photographerError, setPhotographerError] = useState(null);
  const [retryAttempted, setRetryAttempted] = useState(false);

  useEffect(() => {
    if (!photographerId) {
      // No photographer specified - redirect to home
      toast.error("Please access the client portal from your photographer's page");
      navigate("/");
      return;
    }
    fetchPhotographer();
  }, [photographerId, navigate]);

  const fetchPhotographer = async () => {
    setLoadingPhotographer(true);
    setPhotographerError(null);
    
    const run = async () => {
      const response = await axios.get(`${API}/photographer/${photographerId}/public`);
      return response;
    };
    
    try {
      const response = await run();
      setPhotographer(response.data);
      setRetryAttempted(false);
    } catch (err) {
      const status = err?.response?.status;
      
      // If not found, show error UI
      if (status === 404) {
        setPhotographerError({ type: "not_found", message: "Photographer not found." });
        return;
      }
      
      // Transient errors - retry once
      const transientStatuses = [520, 502, 503, 504];
      const isNetworkError = !err?.response;
      
      if (!retryAttempted && (transientStatuses.includes(status) || isNetworkError)) {
        console.warn("Transient error, retrying...", { status, isNetworkError });
        setRetryAttempted(true);
        await new Promise(r => setTimeout(r, 600));
        try {
          const response = await run();
          setPhotographer(response.data);
          setPhotographerError(null);
          return;
        } catch (err2) {
          // fall through to error state
        }
      }
      
      console.warn("SHOWING ERROR STATE DUE TO API ERROR", {
        path: window.location.pathname,
        errorStatus: status,
        isNetworkError
      });
      setPhotographerError({ type: "temporary", message: "We're having trouble loading this page. Please try again." });
    } finally {
      setLoadingPhotographer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/portal/request-access`, {
        email: email,
        photographer_id: photographerId
      });
      setSent(true);
      toast.success("Check your email for the access link!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send access link");
    } finally {
      setLoading(false);
    }
  };

  // Get dynamic branding
  const brandName = photographer?.company_name || photographer?.user?.name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;
  const photographerName = photographer?.user?.name;

  if (loadingPhotographer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error states - show inline UI instead of redirecting
  if (photographerError?.type === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Photographer not found</h1>
          <p className="text-muted-foreground mb-6">
            This photographer profile may have been moved or is unavailable.
          </p>
          <Button
            onClick={() => window.location.href = "/"}
            className="btn-primary"
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  if (photographerError?.type === "temporary") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Could not load this page</h1>
          <p className="text-muted-foreground mb-6">{photographerError.message}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setRetryAttempted(false);
                fetchPhotographer();
              }}
              className="btn-primary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = "/"}
            >
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="client-login-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-serif mb-2">Check Your Email</h1>
            <p className="text-muted-foreground">
              If you have bookings with {photographerName || brandName}, 
              you will receive an email with a secure access link.
            </p>
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The link expires in 24 hours. Check your spam folder if you do not see it.
            </p>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
              >
                Request Another Link
              </Button>
              <Link to={`/photographer/${photographer?.business_name || photographerId}`}>
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to {brandName}
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" data-testid="client-login-page">
      {/* Header */}
      <div className="max-w-md mx-auto mb-8">
        <Link 
          to={`/photographer/${photographer?.business_name || photographerId}`} 
          className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {brandName}
        </Link>
        
        <div className="flex items-center gap-3 mb-6">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-primary" />
              <span className="text-2xl font-serif">{brandName}</span>
            </>
          )}
        </div>
      </div>

      {/* Login Form */}
      <div className="max-w-md mx-auto">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-2xl font-serif">Client Portal</CardTitle>
            <CardDescription>
              Enter your email to access your bookings, galleries, invoices, and contracts with {photographerName || brandName}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email">Your Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Use the same email you used when booking
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || !email}
                data-testid="request-access-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Access Link
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-center text-muted-foreground">
                Do not have a booking yet?{" "}
                <Link to={`/book/${photographerId}`} className="text-primary hover:underline">
                  Book a session
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientLogin;
